<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.loadingMessage"
    button-location="top"
  >
    <template #title>
      Plans
    </template>

    <template #top-form>
      <div
        v-if="canEditPlans"
        class="row top-actions"
      >
        <p-button
          type="purple"
          class="ml-20 mobile-ml-10"
          round
          @click="openCreateDialog"
        >
          New Plan
        </p-button>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search</label>
        <div class="col-sm-6">
          <sanitized-input
            v-model="searchText"
            placeholder="Search by plan name"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Plan Kind</label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="selectedKind"
            :options="kindFilterOptions"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="loadPlans"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="plans"
        :table-columns="tableColumns"
        :highlight-list="highlightList"
        @column-click="handleColumnClick"
      >
        <template #right-fixed-columns>
          <el-table-column
            v-if="canEditPlans"
            fixed="right"
            label="Default"
            width="90"
          >
            <template #default="scope">
              <el-switch
                :model-value="scope.row.isDefault"
                :loading="isDefaultToggleBusy(scope.row.id)"
                @change="(value: boolean) => handleDefaultToggle(value, scope.row)"
              />
            </template>
          </el-table-column>

          <el-table-column
            label="Fragments into"
            minWidth="120"
          >
            <template #default="scope">
              <el-select
                v-if="canEditPlans"
                size="small"
                placeholder=" "
                :modelValue="scope.row.fragmentInto"
                @update:modelValue="(selectedOpt: string) => onFragmentIntoUpdate(selectedOpt, scope)"
              >
                <el-option
                  v-if="scope.row.fragmentInto"
                  label="None (remove)"
                  value="none"
                />
                <el-option
                  v-for="planName in allPlanNames"
                  :key="planName"
                  :label="planName"
                  :value="planName"
                />
              </el-select>
              <span v-else>
                {{ scope.row.fragmentInto }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            label="Defragments into"
            minWidth="120"
          >
            <template #default="scope">
              <el-select
                v-if="canEditPlans"
                size="small"
                placeholder=" "
                :modelValue="scope.row.defragmentInto"
                @update:modelValue="(selectedOpt: string) => onDefragmentIntoUpdate(selectedOpt, scope)"
              >
                <el-option
                  v-if="scope.row.defragmentInto"
                  label="None (remove)"
                  value="none"
                />
                <el-option
                  v-for="planName in allPlanNames"
                  :key="planName"
                  :label="planName"
                  :value="planName"
                />
              </el-select>
              <span v-else>
                {{ scope.row.defragmentInto }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            label="Upgrades into"
            minWidth="120"
          >
            <template #default="scope">
              <el-select
                v-if="canEditPlans"
                size="small"
                placeholder=" "
                :modelValue="scope.row.upgradeInto"
                @update:modelValue="(selectedOpt: string) => onUpgradeIntoUpdate(selectedOpt, scope)"
              >
                <el-option
                  v-if="scope.row.upgradeInto"
                  label="None (remove)"
                  value="none"
                />
                <el-option
                  v-for="planName in allPlanNames"
                  :key="planName"
                  :label="planName"
                  :value="planName"
                />
              </el-select>
              <span v-else>
                {{ scope.row.upgradeInto }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            fixed="right"
            label="Actions"
            width="100"
          >
            <template #default="scope">
              <div class="action-icons">
                <i
                  class="fa fa-eye"
                  title="View details"
                  @click="viewPlan(scope.row)"
                />
                <i
                  v-if="canEditPlans"
                  class="fa fa-edit"
                  title="Edit plan"
                  @click="editPlan(scope.row)"
                />
                <i
                  v-if="canEditPlans"
                  class="fa fa-trash"
                  title="Delete plan"
                  @click="deletePlan(scope.row)"
                />
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>

    <plan-form-dialog
      :model-value="isFormDialogVisible"
      :loading="dialogStatus.isSaving"
      :plan="activePlanForm"
      :all-plan-names="allPlanNames"
      @update:model-value="isFormDialogVisible = $event"
      @submit="submitPlanForm"
    />

    <plan-details-dialog
      :model-value="isDetailsDialogVisible"
      :plan="selectedPlan"
      @update:model-value="isDetailsDialogVisible = $event"
    />
  </card-container>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { debounce } from 'lodash';

import util from '@/Utils/util';
import { defaultPrioritySupportLevel, normalizePrioritySupportLevel, planKindOptions } from '@/constants/plan';
import { useSwalDialog } from '@/use/swalDialog';
import { useProfile } from '@/use/profile';
import { planService } from '@/services/plan';
import { HighlightTableItemType, TableColumnType } from '@/models/table';
import { PlanFormState, PlanListItem, PlanMutationInput, PlanMutationUpdateInput, Plan } from '@/models/plan';
import { PlanKind, PeriodType, type PlanUpdateInput } from '@/gql/graphql';
import { useAllPlanNames } from '@/use/allPlanNames';

const PlanFormDialog = defineAsyncComponent(() => import('./Plans/PlanFormDialog.vue') as any);
const PlanDetailsDialog = defineAsyncComponent(() => import('./Plans/PlanDetailsDialog.vue') as any);

type PlanTableRow = PlanListItem & {
  index: number;
  orderIndex: number;
};

const pageId = 'operator-plan-management';
const pageLimit = 50;

const tableColumns = computed<TableColumnType[]>(() => [
  { field: 'index', label: 'No', width: 70, isDisableSort: true, isCenterAlign: true },
  { field: 'name', label: 'Plan Name', minWidth: 220 },
  {
    field: 'kind',
    label: 'Kind',
    minWidth: 160,
    formatter: (value: string) => util.getLabelByField(planKindOptions, value, 'key') || value,
  },
  {
    field: 'amount',
    label: 'Amount',
    width: 110,
    isCenterAlign: true,
    formatter: (value: number) => util.formatAmountWithCurrency(value || 0),
  },
  {
    field: 'isActive',
    label: 'Active',
    width: 90,
    isCenterAlign: true,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'hasTrial',
    label: 'Has Trial',
    width: 110,
    isCenterAlign: true,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'trialPrice',
    label: 'Trial Price',
    width: 120,
    isCenterAlign: true,
    formatter: (_value: number, row: PlanTableRow) =>
      row.hasTrial && row.trialPrice !== null && row.trialPrice !== undefined ? util.formatAmountWithCurrency(row.trialPrice) : '-',
  },
  {
    field: 'tier',
    label: 'Tier',
    width: 80,
    isCenterAlign: true,
  },
  ...(canEditPlans.value ? [] : [{
    field: 'isDefault',
    label: 'Default',
    width: 100,
    isCenterAlign: true,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  }]),
]);

const status = reactive({
  isLoading: false,
  loadingMessage: '',
});

const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

const highlightList = ref<HighlightTableItemType[]>([]);
const plans = ref<PlanTableRow[]>([]);
const searchText = ref('');
const selectedKind = ref<PlanKind | ''>('');
const kindFilterOptions = [{ id: 0, key: '', label: 'All kinds' }, ...planKindOptions];

const isFormDialogVisible = ref(false);
const dialogStatus = reactive({
  isSaving: false,
});
const activePlanForm = ref<PlanFormState | null>(null);

const isDetailsDialogVisible = ref(false);
const selectedPlan = ref<PlanListItem | Plan | null>(null);

const defaultToggleLoading = ref<string | null>(null);

const { showConfirmPopupDialog, showErrorPopupDialog, showSuccessPopupDialog } = useSwalDialog();
const { userRoles } = useProfile();

const canEditPlans = computed(() => userRoles.isAdmin || userRoles.isDirector);

const isDefaultToggleBusy = (planId: string) => defaultToggleLoading.value === planId;

const allPlanNames = useAllPlanNames();

const updatePlanField = async <K extends keyof PlanUpdateInput, V extends PlanUpdateInput[K]>(planId: string, planName: string, fieldName: K, fieldValue: V) => {
  const confirmed = await showConfirmPopupDialog({
    title: `Are you sure you want to set the ${fieldName} of "${planName}" to "${fieldValue || ''}"?`,
    confirmButtonText: 'Update Plan'
  });

  if (!confirmed) {
    return;
  }

  status.isLoading = true;
  status.loadingMessage = 'Updating plan...';

  try {
    await planService.updatePlan({ id: planId, input: { [fieldName]: fieldValue }});
    showSuccessPopupDialog({
      text: 'Plan updated successfully.'
    })
    await loadPlans(paginationInfo.currentPage);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
    status.loadingMessage = '';
  }
}

const onFragmentIntoUpdate = (selectedOpt: string, scope: any) => {
  updatePlanField(scope.row.id, scope.row.name, 'fragmentInto', selectedOpt === 'none' ? null : selectedOpt);
}

const onDefragmentIntoUpdate = (selectedOpt: string, scope: any) => {
  updatePlanField(scope.row.id, scope.row.name, 'defragmentInto', selectedOpt === 'none' ? null : selectedOpt);
}

const onUpgradeIntoUpdate = (selectedOpt: string, scope: any) => {
  updatePlanField(scope.row.id, scope.row.name, 'upgradeInto', selectedOpt === 'none' ? null : selectedOpt);
}

const createEmptyForm = (): PlanFormState => ({
  name: '',
  kind: PlanKind.Basic,
  amount: 0,
  isActive: false,
  hasTrial: false,
  trialPrice: null,
  tier: 1,
  billingCyclePeriodNum: 1,
  billingCyclePeriodType: PeriodType.Month,
  prioritySupport: defaultPrioritySupportLevel,
  data: '',
  callerDescription: '',
  description: '',
  freeGroceries: null,
  freeRides: null,
  gourmetPaymentTier: undefined,
  groceryPaymentTier: undefined,
  lovedOnesLimit: null,
  minAllowedRole: undefined,
  operatorOrderedRidePrice: null,
  pharmacyPaymentTier: undefined,
  ridePaymentTier: undefined,
  specialRequestPaymentTier: undefined,
  trialPlanName: '',
  activeHours: false,
  authorizedGuestTrips: false,
  dashboard: false,
  deprecated: false,
  emergencyCalls: false,
  favoriteOperators: false,
  goGoCare: false,
  groceryStandingOrder: false,
  homeServices: false,
  insuranceAndTaxStatements: false,
  isAllowedPartnerCaller: false,
  isAllowedRegularCaller: true,
  isAlwaysOperator: false,
  isFreePlan: false,
  isFreePrintedInvoice: false,
  isHighAlerts: false,
  leisureTrips: false,
  proactiveCareManagement: false,
  splitPayments: false,
  turningOffReceipts: false,
  wellnessChecks: false,
});

const formatDataField = (data: any) => {
  if (!data) {
    return '';
  }

  if (typeof data === 'string') {
    return data;
  }

  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};

const mapPlanToForm = (plan: Plan): PlanFormState => ({
  id: plan.id,
  name: plan.name,
  kind: plan.kind,
  amount: plan.amount ?? 0,
  isActive: plan.isActive,
  hasTrial: plan.hasTrial,
  trialPrice: plan.hasTrial ? (plan.trialPrice ?? 0) : null,
  tier: plan.tier ?? 1,
  billingCyclePeriodNum: plan.billingCyclePeriodNum ?? 1,
  billingCyclePeriodType: plan.billingCyclePeriodType,
  prioritySupport: normalizePrioritySupportLevel(plan.prioritySupport),
  data: formatDataField(plan.data),
  callerDescription: plan.callerDescription || '',
  description: plan.description || '',
  freeGroceries: plan.freeGroceries ?? null,
  freeRides: plan.freeRides ?? null,
  gourmetPaymentTier: plan.gourmetPaymentTier ?? undefined,
  groceryPaymentTier: plan.groceryPaymentTier ?? undefined,
  lovedOnesLimit: plan.lovedOnesLimit ?? null,
  minAllowedRole: plan.minAllowedRole ?? undefined,
  operatorOrderedRidePrice: plan.operatorOrderedRidePrice ?? null,
  pharmacyPaymentTier: plan.pharmacyPaymentTier ?? undefined,
  ridePaymentTier: plan.ridePaymentTier ?? undefined,
  specialRequestPaymentTier: plan.specialRequestPaymentTier ?? undefined,
  trialPlanName: plan.trialPlanName || '',
  activeHours: plan.activeHours,
  authorizedGuestTrips: plan.authorizedGuestTrips,
  dashboard: plan.dashboard,
  deprecated: plan.deprecated,
  emergencyCalls: plan.emergencyCalls,
  favoriteOperators: plan.favoriteOperators,
  goGoCare: plan.goGoCare,
  groceryStandingOrder: plan.groceryStandingOrder,
  homeServices: plan.homeServices,
  insuranceAndTaxStatements: plan.insuranceAndTaxStatements,
  isAllowedPartnerCaller: plan.isAllowedPartnerCaller,
  isAllowedRegularCaller: plan.isAllowedRegularCaller,
  isAlwaysOperator: plan.isAlwaysOperator,
  isFreePlan: plan.isFreePlan,
  isFreePrintedInvoice: plan.isFreePrintedInvoice,
  isHighAlerts: plan.isHighAlerts,
  leisureTrips: plan.leisureTrips,
  proactiveCareManagement: plan.proactiveCareManagement,
  splitPayments: plan.splitPayments,
  turningOffReceipts: plan.turningOffReceipts,
  wellnessChecks: plan.wellnessChecks,
  fragmentInto: plan.fragmentInto,
  defragmentInto: plan.defragmentInto,
  upgradeInto: plan.upgradeInto,
});

const parseDataInput = (dataText?: string | null) => {
  if (!dataText) {
    return undefined;
  }

  const trimmed = dataText.trim();

  if (!trimmed) {
    return undefined;
  }

  const error = new Error('Data field must be a valid JSON object.');

  if (!trimmed.startsWith('{')) {
    throw error;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    throw error;
  }
};

const buildSharedInputFields = (form: PlanFormState): Omit<PlanMutationInput, 'name' | 'kind'> => ({
  amount: form.amount ?? 0,
  hasTrial: form.hasTrial,
  trialPrice: form.hasTrial ? (form.trialPrice ?? 0) : null,
  tier: form.tier,
  isActive: form.isActive,
  billingCyclePeriodNum: form.billingCyclePeriodNum,
  billingCyclePeriodType: form.billingCyclePeriodType,
  prioritySupport: normalizePrioritySupportLevel(form.prioritySupport),
  data: parseDataInput(form.data),
  callerDescription: form.callerDescription?.trim() || null,
  description: form.description?.trim() || null,
  freeGroceries: form.freeGroceries !== null && form.freeGroceries !== undefined ? Number(form.freeGroceries) : null,
  freeRides: form.freeRides !== null && form.freeRides !== undefined ? Number(form.freeRides) : null,
  activeHours: form.activeHours,
  authorizedGuestTrips: form.authorizedGuestTrips,
  dashboard: form.dashboard,
  deprecated: form.deprecated,
  emergencyCalls: form.emergencyCalls,
  favoriteOperators: form.favoriteOperators,
  goGoCare: form.goGoCare,
  groceryStandingOrder: form.groceryStandingOrder,
  homeServices: form.homeServices,
  insuranceAndTaxStatements: form.insuranceAndTaxStatements,
  isAllowedPartnerCaller: form.isAllowedPartnerCaller,
  isAllowedRegularCaller: form.isAllowedRegularCaller,
  isAlwaysOperator: form.isAlwaysOperator,
  isFreePlan: form.isFreePlan,
  isFreePrintedInvoice: form.isFreePrintedInvoice,
  isHighAlerts: form.isHighAlerts,
  leisureTrips: form.leisureTrips,
  proactiveCareManagement: form.proactiveCareManagement,
  splitPayments: form.splitPayments,
  turningOffReceipts: form.turningOffReceipts,
  wellnessChecks: form.wellnessChecks,
  lovedOnesLimit: form.lovedOnesLimit !== null && form.lovedOnesLimit !== undefined ? Number(form.lovedOnesLimit) : null,
  minAllowedRole: form.minAllowedRole,
  operatorOrderedRidePrice:
    form.operatorOrderedRidePrice !== null && form.operatorOrderedRidePrice !== undefined ? Number(form.operatorOrderedRidePrice) : null,
  pharmacyPaymentTier: form.pharmacyPaymentTier,
  ridePaymentTier: form.ridePaymentTier,
  specialRequestPaymentTier: form.specialRequestPaymentTier,
  gourmetPaymentTier: form.gourmetPaymentTier,
  groceryPaymentTier: form.groceryPaymentTier,
  trialPlanName: form.trialPlanName?.trim() || null,
  fragmentInto: form.fragmentInto,
  defragmentInto: form.defragmentInto,
  upgradeInto: form.upgradeInto,
});

const buildCreateInput = (form: PlanFormState): PlanMutationInput => ({
  ...buildSharedInputFields(form),
  name: form.name.trim(),
  kind: form.kind,
});

const buildUpdateInput = (form: PlanFormState): PlanMutationUpdateInput => ({
  ...buildSharedInputFields(form),
});

const loadPlans = async (page: number = 1) => {
  status.isLoading = true;
  status.loadingMessage = 'Loading plans...';

  const offset = (page - 1) * pageLimit;
  const nameFilter = searchText.value.trim();
  const kindFilter = selectedKind.value ? (selectedKind.value as PlanKind) : undefined;

  try {
    const response = await planService.fetchPlans({
      kind: kindFilter,
      name: nameFilter || undefined,
      pagination: {
        limit: pageLimit,
        offset,
      },
    });

    paginationInfo.currentPage = page;
    paginationInfo.totalPages = Math.max(Math.ceil(response.count / pageLimit), 1);

    highlightList.value = [];
    plans.value = response.rows.map((plan: PlanListItem, index: number) => {
      if (plan.isDefault) {
        highlightList.value.push({ index, class: 'default-plan-row', type: 'row' });
      }

      return {
        ...plan,
        index: offset + index + 1,
        orderIndex: index,
      } as PlanTableRow;
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
    status.loadingMessage = '';
  }
};

const debouncedLoadPlans = debounce(() => loadPlans(1), 300);

watch(searchText, () => {
  debouncedLoadPlans();
});

watch(selectedKind, () => {
  loadPlans(1);
});

onUnmounted(() => {
  debouncedLoadPlans.cancel();
});

const openCreateDialog = () => {
  activePlanForm.value = createEmptyForm();
  isFormDialogVisible.value = true;
};

const editPlan = async (plan: PlanListItem) => {
  status.isLoading = true;
  status.loadingMessage = 'Loading plan...';

  try {
    const fullPlan = await planService.fetchPlan(plan.name);

    if (!fullPlan) {
      showErrorPopupDialog({
        text: 'Failed to load plan details.',
      });

      return;
    }
    activePlanForm.value = mapPlanToForm(fullPlan);
    isFormDialogVisible.value = true;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
    status.loadingMessage = '';
  }
};

const viewPlan = async (plan: PlanListItem) => {
  status.isLoading = true;
  status.loadingMessage = 'Loading plan details...';

  try {
    const fullPlan = await planService.fetchPlan(plan.name);
    selectedPlan.value = fullPlan ?? plan;
    isDetailsDialogVisible.value = true;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
    status.loadingMessage = '';
  }
};

const deletePlan = async (plan: PlanListItem) => {
  const confirmed = await showConfirmPopupDialog({
    title: `Are you sure you want to delete the "${plan.name}" plan?`,
    confirmButtonText: 'Delete Plan'
  });

  if (!confirmed) {
    return;
  }

  status.isLoading = true;
  status.loadingMessage = 'Deleting plan...';

  try {
    await planService.deletePlan({ id: plan.id });
    showSuccessPopupDialog({
      text: 'Plan deleted successfully.'
    })
    await loadPlans(paginationInfo.currentPage);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
    status.loadingMessage = '';
  }
};

const submitPlanForm = async (formState: PlanFormState) => {
  if (!formState) {
    return;
  }

  const duplicate = plans.value.find((plan) => plan.name.toLowerCase() === formState.name.trim().toLowerCase() && plan.id !== formState.id);

  if (duplicate) {
    showErrorPopupDialog({
      text: 'A plan with this name already exists.',
    });
    return;
  }

  dialogStatus.isSaving = true;
  const isCreate = !formState.id;

  try {
    if (isCreate) {
      const input = buildCreateInput(formState);
      await planService.createPlan({ input });
      showSuccessPopupDialog({
        text: 'Plan created successfully.',
      })
    } else if (formState.id) {
      const input = buildUpdateInput(formState);
      await planService.updatePlan({ id: formState.id, input });
      showSuccessPopupDialog({
        text: 'Plan updated successfully.',
      })
    }

    isFormDialogVisible.value = false;
    await loadPlans(paginationInfo.currentPage);
  } catch (error: any) {
    dialogStatus.isSaving = false;

    if (error instanceof Error && error.message.includes('JSON')) {
      showErrorPopupDialog({
        text: error.message
      });
      return;
    }

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    dialogStatus.isSaving = false;
  }
};

const handleDefaultToggle = async (isDefault: boolean, plan: PlanTableRow) => {
  if (!canEditPlans.value) {
    return;
  }

  if (isDefault === plan.isDefault) {
    return;
  }

  if (!isDefault) {
    await showErrorPopupDialog({
      text: 'A plan stays default until another plan of the same kind is selected as default.',
    });
    await loadPlans(paginationInfo.currentPage);
    return;
  }

  const confirmText = `Set ${plan.name} as the default ${util.getLabelByField(planKindOptions, plan.kind, 'key') || plan.kind} plan?`;

  const confirmed = await showConfirmPopupDialog({
    text: confirmText,
    confirmButtonText: 'Confirm',
  })

  if (!confirmed) {
    await loadPlans(paginationInfo.currentPage);
    return;
  }

  defaultToggleLoading.value = plan.id;
  status.isLoading = true;
  status.loadingMessage = 'Updating default plan...';

  try {
    await planService.setDefaultPlanForKind({ id: plan.id });

    showSuccessPopupDialog({
      text: 'Default plan updated successfully.'
    })
    await loadPlans(paginationInfo.currentPage);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    await loadPlans(paginationInfo.currentPage);
  } finally {
    defaultToggleLoading.value = null;
    status.isLoading = false;
    status.loadingMessage = '';
  }
};

const handleColumnClick = (row: PlanTableRow, column: any) => {
  if (column?.property === 'name') {
    viewPlan(row);
  }
};

onMounted(() => {
  activePlanForm.value = createEmptyForm();
  loadPlans();
});
</script>

<style scoped lang="scss">
.top-actions {
  margin-bottom: 10px;
}

.action-icons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  i {
    cursor: pointer;
    font-size: 18px;
    color: #0b40c1;

    &:hover {
      color: #f96332;
    }
  }
}

:deep(.default-plan-row) {
  background-color: #f2f9ee !important;

  td {
    background-color: #f2f9ee !important;
  }
}
</style>
